import type { BackgroundCheck } from "@repo/models";

import { TLPGrid, useGridColumns } from "@repo/baseCloud/components";
import { styles } from "@repo/helpers";
import { Button, ButtonTypes, MoveableDialog } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { BackgroundChecksFilter } from "./types";

import BackgroundChecksApi, { controller } from "../../api/backgroundChecksApi";
import { QUERY_KEYS } from "../../api/queries";
import { AgentPortal } from "../../apps/agentPortal";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import { BulkImportModal } from "./components/BulkImportModal";
import { ConfigurationsPage } from "./configurations/ConfigurationsPage";
import style from "./style.module.scss";

interface Props {
	filters: BackgroundChecksFilter;
	setFilters: (filters: BackgroundChecksFilter) => void;
	bulkImportEnabled?: boolean;
}

export function BackgroundChecksGrid({ filters, setFilters, bulkImportEnabled }: Props) {
	const [bulkImportOpen, setBulkImportOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedBackgroundCheck, setSelectedBackgroundCheck] = useState<BackgroundCheck | undefined>(undefined);
	const [configDialogOpen, setConfigDialogOpen] = useState(false);

	const backgroundChecksQuery = useQuery({
		queryKey: [QUERY_KEYS.BackgroundChecks.Grid, filters],
		queryFn: ({ signal }) => BackgroundChecksApi.getBackgroundChecks(filters, { signal }),
	});

	const openPortalModal = (bc: BackgroundCheck) => {
		setSelectedBackgroundCheck(bc);
		setDialogOpen(true);
	};

	const { columns, isLoading: isLoadingColumns } = useGridColumns<BackgroundCheck>(
		"BackgroundCheck",
		BackgroundChecksApi.getGridColumns,
		{ openPortalModal },
	);

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<BackgroundCheck, BackgroundChecksFilter>
				id="backgroundChecksGrid"
				title="Background Checks"
				gridNameForExport="BackgroundChecks"
				keyProperty="backgroundCheckId"
				filters={filters}
				setFilters={setFilters}
				query={backgroundChecksQuery}
				apiRoute={controller}
				gridApi={BackgroundChecksApi}
				columns={columns}
				exportFunc={BackgroundChecksApi.getBackgroundChecks}
				additionalActions={
					<>
						<Button types={[ButtonTypes.Primary]} onClick={() => setConfigDialogOpen(true)}>
							Configurations
						</Button>
						{bulkImportEnabled && (
							<Button types={[ButtonTypes.Green]} onClick={() => setBulkImportOpen(true)}>
								Bulk Import
							</Button>
						)}
					</>
				}
				selected={selectedBackgroundCheck?.backgroundCheckId ? [selectedBackgroundCheck.backgroundCheckId] : []}
			/>

			{selectedBackgroundCheck && (
				<MoveableDialog
					open={dialogOpen}
					onOpenChange={() => setDialogOpen(false)}
					title="Rep Portal"
					initialSize={getInitialDialogSize()}
					resizable
					movable
					className={styles("agent-portal-modal", style.noPadding)}
					contentClassName={style.noPadding}
				>
					<AgentPortal
						key={selectedBackgroundCheck?.backgroundCheckId ?? 0}
						repId={selectedBackgroundCheck?.representativeId ?? null}
						selectedTab="BackgroundChecks"
						entityId={selectedBackgroundCheck?.backgroundCheckId}
					/>
				</MoveableDialog>
			)}

			<MoveableDialog
				open={configDialogOpen}
				onOpenChange={() => setConfigDialogOpen(false)}
				title="Configurations"
				initialSize={{ width: 1400, height: 860 }}
				resizable
				movable
			>
				<ConfigurationsPage />
			</MoveableDialog>

			<BulkImportModal
				visible={bulkImportOpen}
				onClose={() => setBulkImportOpen(false)}
				onSuccess={() => backgroundChecksQuery.refetch()}
			/>
		</>
	);
}
